import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  Shield, 
  UserCheck,
  Eye,
  Trash2,
  Edit,
  Clock,
  CheckCircle,
  XCircle,
  UserPlus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (isAdmin()) {
      fetchUsers();
      fetchPendingUsers();
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data.filter(u => u.status !== 'pending'));
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/pending`);
      setPendingUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch pending users:', error);
    }
  };

  const handleApproveUser = async (userId, status) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/users/${userId}/approve`, { status });
      toast.success(`User ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchUsers();
      fetchPendingUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${status} user`);
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateRole = async () => {
    setUpdating(true);
    try {
      await axios.put(`${API}/users/${selectedUser.id}/role?role=${newRole}`);
      toast.success('Role updated successfully');
      fetchUsers();
      setShowRoleDialog(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted');
      setUsers(users.filter(u => u.id !== userId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openRoleDialog = (userItem) => {
    setSelectedUser(userItem);
    setNewRole(userItem.role);
    setShowRoleDialog(true);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-4 h-4" />;
      case 'auditor':
        return <UserCheck className="w-4 h-4" />;
      case 'viewer':
        return <Eye className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-purple-100 text-purple-700 border-0">Admin</Badge>;
      case 'auditor':
        return <Badge className="bg-blue-100 text-blue-700 border-0">Auditor</Badge>;
      case 'viewer':
        return <Badge className="bg-slate-100 text-slate-700 border-0">Viewer</Badge>;
      default:
        return <Badge variant="outline">{role}</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700 border-0">Active</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 border-0">Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 border-0">Rejected</Badge>;
      default:
        return <Badge className="bg-emerald-100 text-emerald-700 border-0">Active</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <Shield className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
        <p className="text-slate-500 mt-2">You need admin privileges to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const stats = {
    total: users.length,
    admins: users.filter(u => u.role === 'admin').length,
    auditors: users.filter(u => u.role === 'auditor').length,
    viewers: users.filter(u => u.role === 'viewer').length,
    pending: pendingUsers.length
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Chivo'] text-slate-900">User Management</h1>
        <p className="text-slate-500 mt-1">Manage user roles, permissions, and access requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                <p className="text-sm text-slate-500">Active Users</p>
              </div>
              <Users className="w-8 h-8 text-slate-200" />
            </div>
          </CardContent>
        </Card>
        <Card className={stats.pending > 0 ? 'border-amber-200 bg-amber-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-2xl font-bold ${stats.pending > 0 ? 'text-amber-600' : 'text-slate-600'}`}>{stats.pending}</p>
                <p className="text-sm text-slate-500">Pending</p>
              </div>
              <Clock className={`w-8 h-8 ${stats.pending > 0 ? 'text-amber-300' : 'text-slate-200'}`} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-purple-600">{stats.admins}</p>
                <p className="text-sm text-slate-500">Admins</p>
              </div>
              <Shield className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.auditors}</p>
                <p className="text-sm text-slate-500">Auditors</p>
              </div>
              <UserCheck className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-slate-600">{stats.viewers}</p>
                <p className="text-sm text-slate-500">Viewers</p>
              </div>
              <Eye className="w-8 h-8 text-slate-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Pending and All Users */}
      <Tabs defaultValue={pendingUsers.length > 0 ? "pending" : "all"} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="pending" className="relative" data-testid="pending-tab">
            Access Requests
            {stats.pending > 0 && (
              <span className="ml-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">{stats.pending}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="all-users-tab">All Users</TabsTrigger>
        </TabsList>

        {/* Pending Access Requests */}
        <TabsContent value="pending" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Chivo'] flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Pending Access Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingUsers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-300" />
                  <p>No pending access requests</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingUsers.map((pendingUser) => (
                    <div 
                      key={pendingUser.id} 
                      className="flex items-center justify-between p-4 bg-amber-50 border border-amber-100 rounded-lg"
                      data-testid={`pending-user-${pendingUser.id}`}
                    >
                      <div>
                        <p className="font-medium text-slate-900">{pendingUser.name}</p>
                        <p className="text-sm text-slate-500">{pendingUser.email}</p>
                        <p className="text-xs text-slate-400 mt-1">Requested {formatDate(pendingUser.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                          onClick={() => handleApproveUser(pendingUser.id, 'rejected')}
                          disabled={updating}
                          data-testid={`reject-${pendingUser.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => handleApproveUser(pendingUser.id, 'approved')}
                          disabled={updating}
                          data-testid={`approve-${pendingUser.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Users */}
        <TabsContent value="all" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Chivo']">All Users</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="border-b">
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((userItem) => (
                      <tr key={userItem.id} data-testid={`user-row-${userItem.id}`}>
                        <td className="font-medium">{userItem.name}</td>
                        <td className="text-slate-500">{userItem.email}</td>
                        <td>{getRoleBadge(userItem.role)}</td>
                        <td>{getStatusBadge(userItem.status)}</td>
                        <td className="text-slate-500">{formatDate(userItem.created_at)}</td>
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRoleDialog(userItem)}
                              data-testid={`edit-role-${userItem.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {userItem.id !== user.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteUser(userItem.id)}
                                data-testid={`delete-user-${userItem.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Role Update Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update User Role</DialogTitle>
            <DialogDescription>
              Change role for {selectedUser?.name} ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-3">
              <Button
                variant={newRole === 'viewer' ? 'default' : 'outline'}
                className={`flex flex-col items-center gap-2 h-auto py-4 ${newRole === 'viewer' ? 'bg-slate-900' : ''}`}
                onClick={() => setNewRole('viewer')}
                data-testid="role-viewer-btn"
              >
                <Eye className="w-5 h-5" />
                <span>Viewer</span>
              </Button>
              <Button
                variant={newRole === 'auditor' ? 'default' : 'outline'}
                className={`flex flex-col items-center gap-2 h-auto py-4 ${newRole === 'auditor' ? 'bg-slate-900' : ''}`}
                onClick={() => setNewRole('auditor')}
                data-testid="role-auditor-btn"
              >
                <UserCheck className="w-5 h-5" />
                <span>Auditor</span>
              </Button>
              <Button
                variant={newRole === 'admin' ? 'default' : 'outline'}
                className={`flex flex-col items-center gap-2 h-auto py-4 ${newRole === 'admin' ? 'bg-slate-900' : ''}`}
                onClick={() => setNewRole('admin')}
                data-testid="role-admin-btn"
              >
                <Shield className="w-5 h-5" />
                <span>Admin</span>
              </Button>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg text-sm text-slate-600">
              <p><strong>Viewer:</strong> Can view completed audits only</p>
              <p><strong>Auditor:</strong> Can create and conduct audits</p>
              <p><strong>Admin:</strong> Full access including user management</p>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowRoleDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="btn-primary"
                onClick={handleUpdateRole}
                disabled={updating || newRole === selectedUser?.role}
                data-testid="update-role-submit-btn"
              >
                {updating ? 'Updating...' : 'Update Role'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
